import * as React from 'react';
import * as styles from './CalendarPickerItems.scss';

import {
    Calendar,
    getDefaultCalendar,
    getSortedCalendarGroups,
    getSortedCalendars,
} from 'owa-calendar-cache';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import {
    calendarPickerShowAllText,
    calendarPickerShowLessText,
    calendarsText,
} from './getCalendarListContextMenuItems.locstring.json';

import type { CalendarGroup } from 'owa-graph-schema';
import { ControlIcons } from 'owa-control-icons';
import type { EventsLoadState } from 'owa-calendar-events-loader';
import { assertNever } from 'owa-assert';
import { colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import getAllAccounts from './getAllAccounts';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import getPrimaryAccount from './getPrimaryAccount';
import { getTaskMenuItem } from './getTaskMenuItem';
import loc from 'owa-localize';

export function getCalendarListContextMenuItems(
    shouldShowAllPickerOptions: boolean,
    shouldShowConnectedAccounts: boolean,
    onSelectCalendar: (
        calendarId: string,
        user: string,
        isSelected: boolean
    ) => (event: React.MouseEvent<HTMLElement>) => void,
    canToggle: (calendar: Calendar, isSelected: boolean) => boolean,
    isCalendarSelected: (calendar: Calendar) => boolean,
    getCalendarLoadingState: (calendar: Calendar) => EventsLoadState,
    onToggleShowAll?: (event: React.MouseEvent<HTMLElement>) => void,
    isCalendarShownCollapsed?: (
        calendar: Calendar,
        userIdentity: string,
        shownCalendarCount: number
    ) => boolean,
    showTasksMenuItem?: (identity: string, group: CalendarGroup) => boolean
): IContextualMenuItem {
    const calendarMenuItems: IContextualMenuItem[] = [];

    const identities = shouldShowConnectedAccounts ? getAllAccounts() : [getPrimaryAccount()];
    identities.forEach(identity => {
        const identityMenuItems = [];
        // Account Header menu item
        if (identities.length > 1) {
            identityMenuItems.push(getAccountHeaderMenuItem(identity));
        }

        let shownCalendars = 0;
        getSortedCalendarGroups(identity).forEach(group => {
            const calendars = getSortedCalendars(identity, group.serverGroupId);
            calendars.forEach(calendar => {
                const isSelected = isCalendarSelected(calendar);
                const isShown =
                    isSelected ||
                    shouldShowAllPickerOptions ||
                    isCalendarShownCollapsed?.(calendar, identity, shownCalendars);
                if (isShown) {
                    shownCalendars++;
                    identityMenuItems.push(
                        getCalendarMenuItem(
                            calendar,
                            identity,
                            isSelected,
                            canToggle,
                            onSelectCalendar,
                            getCalendarLoadingState
                        )
                    );
                }
            });

            if (showTasksMenuItem?.(identity, group)) {
                identityMenuItems.push(getTaskMenuItem());
                shownCalendars++;
            }
        });

        if (shownCalendars > 0) {
            calendarMenuItems.push(...identityMenuItems);
        }
    });

    if (onToggleShowAll) {
        calendarMenuItems.push(
            getShowAllCalendarsMenuItem(shouldShowAllPickerOptions, onToggleShowAll)
        );
    }

    return {
        key: 'Calendars',
        itemType: ContextualMenuItemType.Section,
        role: 'group',
        className: styles.calendarPickerSectionHeader,
        sectionProps: {
            bottomDivider: true,
            title: loc(calendarsText),
            items: calendarMenuItems,
        },
    };
}

//////// ACCOUNT HEADER ////////

function getAccountHeaderMenuItem(user: string): IContextualMenuItem {
    return {
        key: user,
        text: user,
        className: styles.calendarPickerSectionHeader,
        onRender: renderAccountHeaderMenuItem(user),
    };
}

function renderAccountHeaderMenuItem(user: string) {
    return (item: IContextualMenuItem) => {
        return (
            <div className={styles.calendarPickerAccountHeaderContainer} role="group">
                <span className={styles.calendarPickerAccountHeaderLabel}>{user}</span>
            </div>
        );
    };
}

//////// CALENDAR ////////

function getCalendarMenuItem(
    calendar: Calendar,
    user: string,
    isSelected: boolean,
    canToggle: (calendar: Calendar, isSelected: boolean) => boolean,
    onSelectCalendar: (
        calendarId: string,
        user: string,
        isSelected: boolean
    ) => (event: React.MouseEvent<HTMLElement>) => void,
    getCalendarLoadingState: (calendar: Calendar) => EventsLoadState
): IContextualMenuItem {
    const loadingState = getCalendarLoadingState(calendar);
    const canToggleCalendar = canToggle(calendar, isSelected);

    const baseItem: IContextualMenuItem = {
        key: calendar.id.id,
        text: calendar.displayName,
        title: calendar.displayName,
        checked: isSelected,
        disabled: !canToggleCalendar,
        onClick: canToggleCalendar ? onSelectCalendar(calendar.id.id, user, isSelected) : undefined,
        role: 'menuitemcheckbox',
        itemProps: {
            styles: {
                label: styles.calendarPickerCalendarLabel,
            },
        },
        ...getCalendarLoadingIconProps(calendar, user, isSelected, loadingState),
    };

    return baseItem;
}

function getCalendarLoadingIconProps(
    calendar: Calendar,
    user: string,
    isSelected: boolean,
    loadingState: EventsLoadState
): Partial<IContextualMenuItem> {
    const baseItem: Partial<IContextualMenuItem> = {};

    switch (loadingState) {
        case 'NotLoaded':
        case 'Loaded':
            baseItem.iconProps = {
                iconName: isSelected ? ControlIcons.StatusCircleCheckmark : ControlIcons.CircleRing,
                className: styles.calendarIconLoaded,
                style: getCalendarIconColorStyle(calendar, user, isSelected),
            };
            break;
        case 'Loading':
            baseItem.onRenderIcon = renderCalendarIconLoading;
            break;
        case 'Throttled':
        case 'Failed':
            baseItem.onRenderIcon = renderCalendarIconError(calendar, user, isSelected);
            break;
        default:
            return assertNever(loadingState);
    }

    return baseItem;
}

function renderCalendarIconError(calendar: Calendar, user: string, isSelected: boolean) {
    return () => {
        return (
            <span
                className={styles.calendarIconError}
                style={getCalendarIconColorStyle(calendar, user, isSelected)}>
                !
            </span>
        );
    };
}

function renderCalendarIconLoading() {
    return <Spinner className={styles.calendarIconLoading} size={SpinnerSize.small} />;
}

function getCalendarIconColorStyle(calendar: Calendar, user: string, isSelected: boolean) {
    const accountDefaultCalendar = getDefaultCalendar(user);
    const isDefaultCalendar =
        !!accountDefaultCalendar && calendar.id.id === accountDefaultCalendar.id.id;

    const colorScheme = getCalendarColorScheme(calendar.id, isDefaultCalendar);
    return isSelected
        ? colorSchemeToCss(colorScheme)
        : {
              color: colorScheme.calPrimary,
              background: 'transparent',
          };
}

function getShowAllCalendarsMenuItem(
    shouldShowAllPickerOptions: boolean,
    onToggleShowAll: (event: React.MouseEvent<HTMLElement>) => void
): IContextualMenuItem {
    const buttonText = shouldShowAllPickerOptions
        ? loc(calendarPickerShowLessText)
        : loc(calendarPickerShowAllText);

    return {
        key: 'Toggle Calendars',
        text: buttonText,
        title: buttonText,
        onClick: onToggleShowAll,
    };
}
